import PostList from './PostList'

/**
 * Represents a Post object. Used as a wrapper around a post fetched
 * from WordPress to provide more functionality.
 */
export default class Post {
    constructor(post) {
        this.post = JSON.parse(JSON.stringify(post))
        this.setDefaults()
    }

    /**
     * Set defaults and adds properties to the post object.
     */
    setDefaults() {
        this.confirmLinkAndURI()
        this.confirmSlug()
        this.addDefaultProperty('ancestors', [])
        this.addDefaultProperty('isChapterHomePage', this.isChapterHomePage())
        this.addDefaultProperty('isChapterPost', this.isChapterPost())
        this.addDefaultProperty('isNewsArchive', this.isNewsArchive())
        this.addDefaultProperty('isNewsPage', this.isNewsPage())
        this.addDefaultProperty('isCurrentPage', this.isCurrentPage())
        this.addDefaultProperty('menuOrder', 0)

        this.post.hideFromMenu = this.post?.hideFromMenu?.hideFromMenu ?? false
    }

    /**
     * Adds a value to a property if that property does not exist.
     *
     * @param {string} property The property of the post object.
     * @param {any} value  The default property value.
     */
    addDefaultProperty(property, value) {
        this.post[property] = this.post?.[property] ?? value
    }

    /**
     * Make sure the link and post properties are set.
     * As of now, there is no difference between the two.
     */
    confirmLinkAndURI() {
        // We have a link, but no URI
        if (this.post?.link && !this.post?.uri) {
            this.post.uri = this.post.link
        }

        // We have an URI, but no link
        if (this.post?.uri && !this.post?.link) {
            this.post.link = this.post.uri
        }

        // We don't have a link or URI
        if (!this.post?.link && !this.post?.uri) {
            this.post.link = this.post.uri = ''
        }
    }

    /**
     * If slug is not set, retrieve it from the post URI.
     */
    confirmSlug() {
        if (!this.post?.slug) {
            var uri = this.stripTrailingSlash(this.post.uri)
            this.post.slug = uri.substring(uri.lastIndexOf('/') + 1)
        }
    }

    /**
     * Checks to see if the post slug equals 'news'.
     *
     * @returns Boolean
     */
    isNewsArchive() {
        return this.post.slug === 'news'
    }

    /**
     * Checks to see if the post is a news page
     *
     * @returns Boolean
     */
    isNewsPage() {
        return this.post.link.includes('/news/') && !this.isNewsArchive()
    }

    /**
     * If a post has '/groups/' in the link and 'groups' is not the slug,
     * then this post is a part of a chapter.
     *
     * @returns Boolean
     */
    isChapterPost() {
        return (
            this.post.link.includes('/groups/') && this.post.slug !== 'groups'
        )
    }

    /**
     * Checks to see if we have a single ancestor.
     *
     * @returns Boolean
     */
    hasSingleAncestor() {
        return this.post.ancestors?.nodes?.length === 1
    }

    /**
     * Finds the direct (first) parent in the ancestors array.
     *
     * @returns Post object
     */
    getParent() {
        return this.post?.ancestors?.nodes?.[0]
    }

    /**
     * Finds the last parent in the ancestors array.
     *
     * @returns Post object
     */
    getTopParent() {
        return this.post?.ancestors?.nodes?.[
            this.post.ancestors.nodes.length - 1
        ]
    }

    /**
     * Simply returns the post object.
     * @returns Post object
     */
    getPost() {
        return this.post
    }

    /**
     * Checks to see if the page is a chapter home page.
     * @returns Boolean
     */
    isChapterHomePage() {
        return this.hasSingleAncestor() && this.isChapterPost()
    }

    /**
     * Checks to see if the page is the current page in the browser.
     * @returns Boolean
     */
    isCurrentPage() {
        // if (this.hideNews && this.isNewsArchive(post)) {
        //     return true
        // }

        return (
            this.post.link ===
            (typeof window !== 'undefined' && window.location.pathname)
        )
    }

    /**
     * Removes '/' from the end of a string.
     *
     * @param {string} url
     * @returns string without a trailins slash
     */
    stripTrailingSlash(url) {
        return url.endsWith('/') ? url.slice(0, -1) : url
    }

    /**
     * Get a list of posts from a specified root.
     * @param {*} root
     * @param {*} options
     * @returns
     */
    getPostList(root, options) {
        var postList = new PostList(root, options)
        return postList.getList()
    }
}
